<template>
  <section class="skills-section modern-skills-section">
    <div class="section-container">
      <div class="section-header">
        <h2 class="section-title modern-title">Technical Skills</h2>
        <p class="section-description modern-description">
          Technologies, frameworks, and tools I work with
        </p>
        <div class="section-divider"></div>
      </div>

      <!-- Skills Filter/Category Tabs -->
      <div class="skills-filter">
        <v-chip-group
          v-model="activeCategory"
          class="category-chips"
          mandatory
          active-class="active-chip"
        >
          <v-chip
            v-for="(category, idx) in skillCategories"
            :key="idx"
            class="category-chip"
            outlined
          >
            <v-icon left size="16">{{ category.icon }}</v-icon>
            {{ category.name }}
          </v-chip>
        </v-chip-group>
      </div>

      <!-- Skills Grid/Cards Layout -->
      <div class="skills-grid">
        <transition-group name="skill-card" tag="div" class="skills-container">
          <div
            v-for="(skill, index) in filteredSkills"
            :key="skill.title"
            class="skill-card modern-skill-card"
            :class="{
              'is-visible': isVisible[index],
              'is-hovered': skill.isHovered
            }"
            :style="getSkillCardStyle(skill, index)"
            @mouseenter="handleSkillHover(skill, true)"
            @mouseleave="handleSkillHover(skill, false)"
            @click="showSkillDetails(skill)"
          >
            <!-- Skill Icon with Glow Effect -->
            <div class="skill-icon-container" :style="getIconContainerStyle(skill)">
              <div class="skill-icon-glow" :style="getIconGlowStyle(skill)"></div>
              <v-icon
                :color="skill.color"
                size="40"
                class="skill-icon modern-skill-icon"
              >
                {{ skill.icon }}
              </v-icon>

              <!-- Skill Level Ring -->
              <div class="skill-level-ring" :style="getSkillRingStyle(skill)">
                <svg class="level-circle" width="60" height="60">
                  <circle
                    cx="30"
                    cy="30"
                    r="25"
                    fill="none"
                    :stroke="skill.color + '30'"
                    stroke-width="3"
                  />
                  <circle
                    cx="30"
                    cy="30"
                    r="25"
                    fill="none"
                    :stroke="skill.color"
                    stroke-width="3"
                    stroke-linecap="round"
                    :stroke-dasharray="getSkillDashArray()"
                    :stroke-dashoffset="getSkillDashOffset(skill.level || 80)"
                    class="level-progress"
                  />
                </svg>
              </div>
            </div>

            <!-- Skill Content -->
            <div class="skill-content">
              <h4 class="skill-title">{{ skill.title }}</h4>

              <!-- Skill Level Badge -->
              <div class="skill-level">
                <v-chip
                  x-small
                  :color="getSkillLevelColor(skill.level || 80)"
                  class="level-badge"
                  text-color="white"
                >
                  {{ getSkillLevelText(skill.level || 80) }}
                </v-chip>
              </div>

              <!-- Years of Experience -->
              <div v-if="skill.years" class="skill-years">
                <v-icon small color="grey" class="years-icon">mdi-clock-outline</v-icon>
                <span class="years-text">{{ skill.years }} years</span>
              </div>

              <!-- Skill Description -->
              <p v-if="skill.description" class="skill-description">
                {{ skill.description }}
              </p>

              <!-- Related Projects Count -->
              <div v-if="skill.projects" class="skill-projects">
                <v-icon small color="primary" class="projects-icon">mdi-folder-multiple</v-icon>
                <span class="projects-text">{{ skill.projects }} projects</span>
              </div>

              <!-- Skill Progress Bar (Alternative Visual) -->
              <div class="skill-progress-bar">
                <div
                  class="progress-fill"
                  :style="getProgressBarStyle(skill)"
                ></div>
              </div>
            </div>

            <!-- Hover Overlay with Quick Actions -->
            <div class="skill-overlay">
              <div class="overlay-actions">
                <v-btn
                  icon
                  small
                  color="white"
                  @click.stop="showSkillProjects(skill)"
                  title="View Projects"
                >
                  <v-icon size="16">mdi-folder-open</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  color="white"
                  @click.stop="showSkillCertifications(skill)"
                  title="Certifications"
                >
                  <v-icon size="16">mdi-certificate</v-icon>
                </v-btn>
              </div>
            </div>

            <!-- Card Shine Effect -->
            <div class="card-shine" :style="getCardShineStyle(index)"></div>
          </div>
        </transition-group>
      </div>

      <!-- Skills Statistics -->
      <div class="skills-stats">
        <div class="stats-container">
          <div class="stat-item">
            <div class="stat-icon">
              <v-icon color="primary" size="24">mdi-code-tags</v-icon>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ getTotalSkills() }}</div>
              <div class="stat-label">Technologies</div>
            </div>
          </div>

          <div class="stat-item">
            <div class="stat-icon">
              <v-icon color="success" size="24">mdi-star</v-icon>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ getExpertSkills() }}</div>
              <div class="stat-label">Expert Level</div>
            </div>
          </div>

          <div class="stat-item">
            <div class="stat-icon">
              <v-icon color="info" size="24">mdi-trending-up</v-icon>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ getAverageExperience() }}</div>
              <div class="stat-label">Avg. Years</div>
            </div>
          </div>

          <div class="stat-item">
            <div class="stat-icon">
              <v-icon color="warning" size="24">mdi-layers</v-icon>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ getSkillCategories().length }}</div>
              <div class="stat-label">Categories</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Skills Proficiency Chart -->
      <div class="skills-chart">
        <h3 class="chart-title">Skill Proficiency Overview</h3>
        <div class="proficiency-chart">
          <div
            v-for="category in skillCategories"
            :key="category.key"
            class="category-bar"
          >
            <div class="category-info">
              <v-icon :color="category.color" size="20">{{ category.icon }}</v-icon>
              <span class="category-name">{{ category.name }}</span>
            </div>
            <div class="category-progress">
              <div
                class="category-fill"
                :style="getCategoryProgressStyle(category)"
              ></div>
            </div>
            <div class="category-percentage">
              {{ getCategoryAverage(category.key) }}%
            </div>
          </div>
        </div>
      </div>

      <!-- Learning Path / Roadmap -->
      <div class="learning-path">
        <h3 class="path-title">Continuous Learning</h3>
        <div class="learning-items">
          <div
            v-for="(item, index) in learningPath"
            :key="index"
            class="learning-item"
            :class="item.status"
          >
            <div class="learning-icon">
              <v-icon :color="getLearningStatusColor(item.status)">
                {{ getLearningStatusIcon(item.status) }}
              </v-icon>
            </div>
            <div class="learning-content">
              <h4 class="learning-title">{{ item.title }}</h4>
              <p class="learning-description">{{ item.description }}</p>
              <div class="learning-progress" v-if="item.progress">
                <div class="progress-bar">
                  <div
                    class="progress-value"
                    :style="{ width: item.progress + '%', backgroundColor: getLearningStatusColor(item.status) }"
                  ></div>
                </div>
                <span class="progress-text">{{ item.progress }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Skill Details Modal/Dialog -->
    <v-dialog v-model="showSkillModal" max-width="500">
      <v-card v-if="selectedSkill" class="skill-modal">
        <v-card-title class="skill-modal-header" :style="getModalHeaderStyle(selectedSkill)">
          <v-icon :color="selectedSkill.color" size="32" class="mr-3">
            {{ selectedSkill.icon }}
          </v-icon>
          <span>{{ selectedSkill.title }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="showSkillModal = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="skill-modal-content">
          <div class="modal-skill-level">
            <h4>Proficiency Level</h4>
            <v-progress-linear
              :value="selectedSkill.level || 80"
              :color="selectedSkill.color"
              height="8"
              rounded
            ></v-progress-linear>
            <div class="level-text">{{ getSkillLevelText(selectedSkill.level || 80) }}</div>
          </div>

          <div v-if="selectedSkill.description" class="modal-description">
            <h4>About</h4>
            <p>{{ selectedSkill.description }}</p>
          </div>

          <div v-if="selectedSkill.years" class="modal-experience">
            <h4>Experience</h4>
            <p>{{ selectedSkill.years }} years of hands-on experience</p>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
export default {
  name: "TechnicalSkillsSection",
  props: {
    skills: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  data() {
    return {
      isVisible: [],
      activeCategory: 0,
      showSkillModal: false,
      selectedSkill: null,
      skillCategories: [
        {
          key: 'all',
          name: 'All Skills',
          icon: 'mdi-code-tags',
          color: '#4361ee'
        },
        {
          key: 'languages',
          name: 'Languages',
          icon: 'mdi-language-python',
          color: '#f72585'
        },
        {
          key: 'frontend',
          name: 'Frontend',
          icon: 'mdi-monitor',
          color: '#4cc9f0'
        },
        {
          key: 'backend',
          name: 'Backend',
          icon: 'mdi-server',
          color: '#7209b7'
        },
        {
          key: 'tools',
          name: 'Tools',
          icon: 'mdi-tools',
          color: '#f77f00'
        }
      ],
      learningPath: [
        {
          title: 'Advanced TypeScript',
          description: 'Deepening knowledge in advanced TypeScript patterns',
          status: 'in-progress',
          progress: 65
        },
        {
          title: 'Docker & Kubernetes',
          description: 'Container orchestration and deployment',
          status: 'planned',
          progress: 20
        },
        {
          title: 'GraphQL',
          description: 'Modern API query language',
          status: 'completed',
          progress: 100
        },
        {
          title: 'AWS Certifications',
          description: 'Cloud infrastructure and services',
          status: 'in-progress',
          progress: 40
        }
      ]
    };
  },
  computed: {
    filteredSkills() {
      const category = this.skillCategories[this.activeCategory];
      if (category.key === 'all') {
        return this.enhancedSkills;
      }
      return this.enhancedSkills.filter(skill => skill.category === category.key);
    },

    enhancedSkills() {
      return this.skills.map(skill => ({
        ...skill,
        category: skill.category || this.categorizeSkill(skill.title),
        level: skill.level || this.generateSkillLevel(skill.title),
        years: skill.years || this.generateExperienceYears(skill.title),
        description: skill.description || this.generateSkillDescription(skill.title),
        projects: skill.projects || this.generateProjectCount(),
        isHovered: false
      }));
    }
  },
  methods: {
    categorizeSkill(skillTitle) {
      const skill = skillTitle.toLowerCase().trim();

      const languages = ['python', 'java', 'c++', 'c#', 'javascript'];
      const frontend = ['vue', 'react', 'angular', 'html5', 'html', 'css3', 'css', 'vuetify', 'webpack', 'sass', 'testing'];
      const backend = ['flask', 'restful apis', 'restful', 'api', 'node', 'nodejs'];

      if (languages.includes(skill)) {
        return 'languages';
      }

      if (frontend.some(f => skill.includes(f))) {
        return 'frontend';
      }

      if (backend.some(b => skill.includes(b))) {
        return 'backend';
      }

      return 'tools';
    },

    generateSkillLevel(skillTitle) {
      // Generate realistic skill levels based on skill type
      const levels = {
        'vue': 95,
        'javascript': 90,
        'python': 85,
        'react': 80,
        'html5': 95,
        'css3': 90,
        'git': 85,
        'c++': 75,
        'java': 70,
        'angular': 65
      };
      return levels[skillTitle.toLowerCase()] || Math.floor(Math.random() * 30) + 70;
    },

    generateExperienceYears(skillTitle) {
      const experience = {
        'vue': '4+',
        'javascript': '5+',
        'python': '4+',
        'html5': '5+',
        'css3': '5+',
        'react': '3+',
        'git': '4+',
        'c++': '3+',
        'java': '2+'
      };
      return experience[skillTitle.toLowerCase()] || '2+';
    },

    generateSkillDescription(skillTitle) {
      const descriptions = {
        'Vue': 'Progressive JavaScript framework for building user interfaces',
        'React': 'JavaScript library for building component-based UIs',
        'Python': 'Versatile programming language for backend and data science',
        'JavaScript': 'Dynamic programming language for web development',
        'HTML5': 'Latest markup language for structuring web content',
        'CSS3': 'Styling language for modern web design',
        'Git': 'Distributed version control system'
      };
      return descriptions[skillTitle] || `Professional experience with ${skillTitle}`;
    },

    generateProjectCount() {
      return Math.floor(Math.random() * 15) + 5;
    },

    handleSkillHover(skill, isHovered) {
      this.$set(skill, 'isHovered', isHovered);
    },

    showSkillDetails(skill) {
      this.selectedSkill = skill;
      this.showSkillModal = true;
    },

    showSkillProjects(skill) {
      // Implement project showcase
      console.log(`Showing projects for ${skill.title}`);
    },

    showSkillCertifications(skill) {
      // Implement certifications view
      console.log(`Showing certifications for ${skill.title}`);
    },

    getSkillCardStyle(skill, index) {
      const delays = [0, 100, 200, 300, 400];
      return {
        '--hover-color': skill.color,
        '--animation-delay': `${delays[index % delays.length]}ms`
      };
    },

    getIconContainerStyle(skill) {
      return {
        '--skill-color': skill.color
      };
    },

    getIconGlowStyle(skill) {
      return {
        background: `radial-gradient(circle, ${skill.color}30, transparent 70%)`
      };
    },

    getSkillRingStyle(skill) {
      return {
        '--skill-color': skill.color
      };
    },

    getSkillDashArray() {
      const circumference = 2 * Math.PI * 25; // radius = 25
      return `${circumference} ${circumference}`;
    },

    getSkillDashOffset(level) {
      const circumference = 2 * Math.PI * 25;
      return circumference - (level / 100) * circumference;
    },

    getSkillLevelColor(level) {
      if (level >= 90) return 'success';
      if (level >= 75) return 'primary';
      if (level >= 60) return 'warning';
      return 'orange';
    },

    getSkillLevelText(level) {
      if (level >= 90) return 'Expert';
      if (level >= 75) return 'Advanced';
      if (level >= 60) return 'Intermediate';
      return 'Beginner';
    },

    getProgressBarStyle(skill) {
      return {
        width: `${skill.level || 80}%`,
        backgroundColor: skill.color
      };
    },

    getCardShineStyle(index) {
      const delay = index * 200;
      return {
        animationDelay: `${delay}ms`
      };
    },

    getModalHeaderStyle(skill) {
      return {
        background: `linear-gradient(135deg, ${skill.color}20, ${skill.color}10)`
      };
    },

    getCategoryProgressStyle(category) {
      const average = this.getCategoryAverage(category.key);
      return {
        width: `${average}%`,
        backgroundColor: category.color
      };
    },

    getCategoryAverage(categoryKey) {
      if (categoryKey === 'all') {
        const total = this.enhancedSkills.reduce((sum, skill) => sum + skill.level, 0);
        return Math.round(total / this.enhancedSkills.length);
      }

      const categorySkills = this.enhancedSkills.filter(skill => skill.category === categoryKey);
      if (categorySkills.length === 0) return 0;

      const total = categorySkills.reduce((sum, skill) => sum + skill.level, 0);
      return Math.round(total / categorySkills.length);
    },

    getLearningStatusColor(status) {
      const colors = {
        'completed': '#4caf50',
        'in-progress': '#2196f3',
        'planned': '#ff9800'
      };
      return colors[status] || '#666';
    },

    getLearningStatusIcon(status) {
      const icons = {
        'completed': 'mdi-check-circle',
        'in-progress': 'mdi-progress-clock',
        'planned': 'mdi-calendar-clock'
      };
      return icons[status] || 'mdi-circle';
    },

    getTotalSkills() {
      return this.skills.length;
    },

    getExpertSkills() {
      return this.enhancedSkills.filter(skill => skill.level >= 90).length;
    },

    getAverageExperience() {
      const totalYears = this.enhancedSkills.reduce((sum, skill) => {
        const years = parseInt(skill.years) || 0;
        return sum + years;
      }, 0);
      return Math.round(totalYears / this.enhancedSkills.length * 10) / 10;
    },

    getSkillCategories() {
      return this.skillCategories.filter(cat => cat.key !== 'all');
    },

    handleScroll() {
      const items = document.querySelectorAll('.skill-card');
      items.forEach((item, index) => {
        if (this.isInViewport(item)) {
          this.$set(this.isVisible, index, true);
        }
      });
    },

    isInViewport(element) {
      const rect = element.getBoundingClientRect();
      return rect.top >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);
    }
  },

  mounted() {
    // Initialize visibility states
    this.isVisible = new Array(this.skills.length).fill(false);

    // Initialize hover states for enhanced skills
    this.$nextTick(() => {
      this.enhancedSkills.forEach(skill => {
        this.$set(skill, 'isHovered', false);
      });
    });

    // Add scroll listener for visibility detection
    window.addEventListener('scroll', this.handleScroll);
    this.$nextTick(this.handleScroll);
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  }
};
</script>

<style lang="scss" scoped>
@import "@/styles/variables.scss";

.skills-section {
  padding: 4rem 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background:
      radial-gradient(circle at 80% 20%, rgba(76, 201, 240, 0.06), transparent 50%),
      radial-gradient(circle at 20% 80%, rgba(114, 9, 183, 0.08), transparent 50%);
    pointer-events: none;
  }
}

.section-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
}

.section-header {
  text-align: center;
  margin-bottom: 3rem;
}

.modern-title {
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, $primary, $accent, #4cc9f0);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-family: 'Poppins', sans-serif;
  text-shadow: 0 4px 20px rgba(76, 201, 240, 0.3);

  .theme--light & {
    text-shadow: 0 2px 10px rgba(76, 201, 240, 0.2);
  }

  @media (max-width: $breakpoint-xs) {
    font-size: 2.2rem;
  }
}

.modern-description {
  max-width: 600px;
  margin: 0 auto 2rem;
  font-size: 1.2rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 400;

  .theme--light & {
    color: rgba(73, 80, 87, 0.8);
  }
}

.section-divider {
  width: 120px;
  height: 4px;
  background: linear-gradient(90deg, $primary, $accent, #4cc9f0, #7209b7);
  margin: 0 auto;
  border-radius: 2px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(90deg, $primary, $accent, #4cc9f0, #7209b7);
    border-radius: 4px;
    opacity: 0.3;
    filter: blur(4px);
  }
}

// Skills Filter
.skills-filter {
  display: flex;
  justify-content: center;
  margin-bottom: 3rem;
  width: 100%;
  pointer-events: auto;
  position: relative;
  z-index: 10;
}

.category-chips {
  display: flex;
  gap: 0.8rem;
  overflow-x: auto;
  padding: 0.5rem 0;
  scroll-behavior: smooth;
  max-width: 100%;
  pointer-events: auto;

  &::-webkit-scrollbar {
    height: 0;
  }

  scrollbar-width: none;
}

.category-chip {
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.08) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
  flex-shrink: 0;
  min-width: fit-content;
  cursor: pointer;
  pointer-events: auto;

  .theme--light & {
    background: rgba(255, 255, 255, 0.9) !important;
    border: 1px solid rgba(67, 97, 238, 0.12) !important;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(67, 97, 238, 0.2);
  }

  &.active-chip {
    background: linear-gradient(135deg, $primary, $accent) !important;
    color: white !important;
    border: 1px solid $primary !important;
  }
}

// Skills Grid
.skills-grid {
  margin-bottom: 4rem;
}

.skills-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  padding: 1rem 0;
}

.skill-card {
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(25px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  padding: 2rem;
  text-align: center;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  opacity: 0;
  transform: scale(0.8) translateY(30px);

  .theme--light & {
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(67, 97, 238, 0.08);
    box-shadow: 0 4px 20px rgba(67, 97, 238, 0.08);
  }

  &.is-visible {
    opacity: 1;
    transform: scale(1) translateY(0);
  }

  &:hover,
  &.is-hovered {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
    border-color: var(--hover-color, $primary);

    .skill-icon-glow {
      opacity: 1;
      transform: scale(1.2);
    }

    .skill-overlay {
      opacity: 1;
    }

    .card-shine {
      opacity: 1;
      animation: shine 2s ease-in-out infinite;
    }

    .level-progress {
      stroke-dasharray: 157 157;
      animation: progressFill 1.5s ease-out;
    }
  }
}

// Skill Icon Container
.skill-icon-container {
  position: relative;
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.skill-icon-glow {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  opacity: 0;
  transition: all 0.3s ease;
  filter: blur(10px);
}

.modern-skill-icon {
  z-index: 2;
  position: relative;
}

.skill-level-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
}

.level-circle {
  transform: rotate(-90deg);
}

.level-progress {
  transition: stroke-dashoffset 0.8s ease;
}

@keyframes progressFill {
  from {
    stroke-dashoffset: 157;
  }
  to {
    stroke-dashoffset: var(--final-offset);
  }
}

// Skill Content
.skill-content {
  position: relative;
  z-index: 2;
}

.skill-title {
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 0.8rem;
  color: rgba(255, 255, 255, 0.95);
  font-family: 'Poppins', sans-serif;

  .theme--light & {
    color: rgba(33, 37, 41, 0.9);
  }
}

.skill-level {
  margin-bottom: 0.8rem;
}

.level-badge {
  font-size: 0.7rem;
  font-weight: 600;
  border-radius: 8px;
}

.skill-years {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  margin-bottom: 0.8rem;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);

  .theme--light & {
    color: rgba(73, 80, 87, 0.7);
  }
}

.skill-description {
  font-size: 0.85rem;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.8rem;

  .theme--light & {
    color: rgba(73, 80, 87, 0.7);
  }
}

.skill-projects {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  margin-bottom: 1rem;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.8);

  .theme--light & {
    color: rgba(73, 80, 87, 0.8);
  }
}

.skill-progress-bar {
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;

  .theme--light & {
    background: rgba(67, 97, 238, 0.1);
  }
}

.progress-fill {
  height: 100%;
  transition: width 0.8s ease;
  border-radius: 2px;
}

// Skill Overlay
.skill-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(67, 97, 238, 0.9), rgba(247, 37, 133, 0.9));
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.3s ease;
  border-radius: 20px;
}

.overlay-actions {
  display: flex;
  gap: 1rem;
}

// Card Shine Effect
.card-shine {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(
    45deg,
    transparent,
    rgba(255, 255, 255, 0.1),
    transparent
  );
  opacity: 0;
  transition: opacity 0.3s ease;
  transform: rotate(45deg);
}

@keyframes shine {
  0% {
    transform: translateX(-100%) translateY(-100%) rotate(45deg);
  }
  100% {
    transform: translateX(100%) translateY(100%) rotate(45deg);
  }
}

// Skill Card Transitions
.skill-card-enter-active,
.skill-card-leave-active {
  transition: all 0.5s ease;
}

.skill-card-enter,
.skill-card-leave-to {
  opacity: 0;
  transform: scale(0.8) translateY(30px);
}

// Skills Statistics
.skills-stats {
  margin-bottom: 3rem;
}

.stats-container {
  display: flex;
  justify-content: center;
  gap: 3rem;
  flex-wrap: wrap;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  backdrop-filter: blur(10px);

  .theme--light & {
    background: rgba(255, 255, 255, 0.8);
  }
}

.stat-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);

  .theme--light & {
    background: rgba(67, 97, 238, 0.1);
  }
}

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-number {
  font-size: 1.5rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1;

  .theme--light & {
    color: rgba(33, 37, 41, 0.9);
  }
}

.stat-label {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;

  .theme--light & {
    color: rgba(73, 80, 87, 0.7);
  }
}

// Skills Proficiency Chart
.skills-chart {
  margin-bottom: 3rem;
}

.chart-title {
  text-align: center;
  font-size: 1.5rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
  font-family: 'Poppins', sans-serif;

  .theme--light & {
    color: rgba(33, 37, 41, 0.9);
  }
}

.proficiency-chart {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.category-bar {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  backdrop-filter: blur(10px);

  .theme--light & {
    background: rgba(255, 255, 255, 0.8);
  }
}

.category-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 120px;
}

.category-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);

  .theme--light & {
    color: rgba(33, 37, 41, 0.9);
  }
}

.category-progress {
  flex: 1;
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;

  .theme--light & {
    background: rgba(67, 97, 238, 0.1);
  }
}

.category-fill {
  height: 100%;
  transition: width 1s ease;
  border-radius: 4px;
}

.category-percentage {
  min-width: 40px;
  text-align: right;
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);

  .theme--light & {
    color: rgba(73, 80, 87, 0.8);
  }
}

// Learning Path
.learning-path {
  margin-bottom: 2rem;
}

.path-title {
  text-align: center;
  font-size: 1.5rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
  font-family: 'Poppins', sans-serif;

  .theme--light & {
    color: rgba(33, 37, 41, 0.9);
  }
}

.learning-items {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.learning-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  backdrop-filter: blur(10px);
  border-left: 4px solid transparent;
  transition: all 0.3s ease;

  .theme--light & {
    background: rgba(255, 255, 255, 0.8);
  }

  &.completed {
    border-left-color: #4caf50;
  }

  &.in-progress {
    border-left-color: #2196f3;
  }

  &.planned {
    border-left-color: #ff9800;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
}

.learning-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  flex-shrink: 0;

  .theme--light & {
    background: rgba(67, 97, 238, 0.1);
  }
}

.learning-content {
  flex: 1;
}

.learning-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: rgba(255, 255, 255, 0.95);

  .theme--light & {
    color: rgba(33, 37, 41, 0.9);
  }
}

.learning-description {
  font-size: 0.85rem;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 1rem;

  .theme--light & {
    color: rgba(73, 80, 87, 0.7);
  }
}

.learning-progress {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.progress-bar {
  flex: 1;
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;

  .theme--light & {
    background: rgba(67, 97, 238, 0.1);
  }
}

.progress-value {
  height: 100%;
  transition: width 0.8s ease;
  border-radius: 3px;
}

.progress-text {
  font-size: 0.8rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  min-width: 35px;

  .theme--light & {
    color: rgba(73, 80, 87, 0.8);
  }
}

// Skill Modal
.skill-modal {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(25px);
  border-radius: 16px !important;

  .theme--dark & {
    background: rgba(26, 26, 46, 0.95) !important;
  }
}

.skill-modal-header {
  border-radius: 16px 16px 0 0 !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;

  .theme--light & {
    border-color: rgba(67, 97, 238, 0.1) !important;
  }
}

.skill-modal-content {
  padding: 2rem !important;
}

.modal-skill-level,
.modal-description,
.modal-experience {
  margin-bottom: 1.5rem;

  h4 {
    font-size: 0.9rem;
    font-weight: 600;
    margin-bottom: 0.8rem;
    color: rgba(33, 37, 41, 0.9);

    .theme--dark & {
      color: rgba(255, 255, 255, 0.9);
    }
  }

  p {
    color: rgba(73, 80, 87, 0.8);
    line-height: 1.5;

    .theme--dark & {
      color: rgba(255, 255, 255, 0.8);
    }
  }
}

.level-text {
  text-align: center;
  margin-top: 0.5rem;
  font-weight: 600;
  color: rgba(33, 37, 41, 0.8);

  .theme--dark & {
    color: rgba(255, 255, 255, 0.8);
  }
}

// Responsive Design
@media (max-width: $breakpoint-lg) {
  .skills-container {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1rem;
  }

  .stats-container {
    gap: 2rem;
  }
}

@media (max-width: $breakpoint-md) {
  .modern-title {
    font-size: 2.5rem;
  }

  .skills-container {
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  }

  .stats-container {
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
  }

  .category-bar {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.8rem;
  }

  .category-info {
    min-width: auto;
  }

  .category-progress {
    width: 100%;
  }
}

@media (max-width: $breakpoint-xs) {
  .skills-section {
    padding: 2rem 0;
  }

  .section-container {
    padding: 0 1rem;
  }

  .modern-title {
    font-size: 2rem;
  }

  .modern-description {
    font-size: 1rem;
  }

  .skills-container {
    grid-template-columns: 1fr;
  }

  .skill-card {
    padding: 1.5rem;
  }

  .learning-items {
    grid-template-columns: 1fr;
  }
}

// Dark/Light theme specific adjustments
.theme--light {
  .skill-card {
    &:hover {
      box-shadow: 0 12px 28px rgba(67, 97, 238, 0.15);
    }
  }

  .card-shine {
    background: linear-gradient(
      45deg,
      transparent,
      rgba(67, 97, 238, 0.1),
      transparent
    );
  }
}
</style>